import React, { useEffect, useRef, useState } from 'react'

interface PasteClipChipProps {
  label: string
  text: string
  lineCount: number
  /** Omit for read-only chips (e.g. in a sent message bubble). */
  onRemove?: () => void
}

/**
 * Collapsed preview for a large paste dropped into the composer (Claude/Replit-style
 * "clip"). Keeps the textarea from ballooning to fit thousands of pasted characters —
 * clicking the chip reveals the full text in a scrollable popover instead.
 */
const PasteClipChip: React.FC<PasteClipChipProps> = ({ label, text, lineCount, onRemove }) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div
      ref={containerRef}
      className="tw-relative tw-inline-block"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-pl-2 tw-pr-3 tw-py-1.5 tw-text-xs tw-text-gray-800 tw-max-w-[200px] tw-shadow-sm tw-cursor-pointer"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-bg-gray-100 tw-text-gray-500 tw-flex-shrink-0">
          <svg className="tw-w-3.5 tw-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M9 8h1m-5 12h14a2 2 0 002-2V6a2 2 0 00-2-2H8.828a2 2 0 00-1.414.586L4.586 7.414A2 2 0 004 8.828V18a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="tw-flex tw-flex-col tw-min-w-0 tw-text-left">
          <span className="tw-truncate tw-font-medium tw-text-gray-700">{label}</span>
          <span className="tw-text-[9px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{lineCount} lines</span>
        </div>
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="tw-absolute tw--top-1.5 tw--right-1.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-gray-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-cursor-pointer tw-border-0 hover:tw-bg-red-500 tw-transition-colors"
        >
          &times;
        </button>
      )}
      {open && (
        <div className="tw-absolute tw-bottom-full tw-left-0 tw-mb-2 tw-z-50 tw-w-80 tw-max-h-72 tw-overflow-y-auto tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2">
            <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-truncate">{label}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false) }}
              className="tw-text-gray-400 hover:tw-text-gray-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-sm tw-leading-none tw-flex-shrink-0"
            >
              &times;
            </button>
          </div>
          <pre className="tw-whitespace-pre-wrap tw-break-words tw-text-xs tw-text-gray-800 tw-font-mono tw-m-0">{text}</pre>
        </div>
      )}
    </div>
  )
}

export default PasteClipChip
